import { Controller } from '@hotwired/stimulus'
import { createPopper } from '@popperjs/core'

export default class extends Controller {
  static targets = ['menu']
  static values = {
    placement: { type: String, default: 'bottom' },
    offset: { type: Number, default: 8 },
    alignment: { type: String, default: 'left' },
  }

  connect() {
    this.boundHandleClickOutside = this.handleClickOutside.bind(this)
    this.boundHandleKeydown = this.handleKeydown.bind(this)

    document.addEventListener('click', this.boundHandleClickOutside)
    document.addEventListener('keydown', this.boundHandleKeydown)

    this.triggerEl = this.element.querySelector('button')
    if (this.triggerEl && this.hasMenuTarget && this.menuTarget.id) {
      this.triggerEl.setAttribute('aria-haspopup', 'menu')
      this.triggerEl.setAttribute('aria-controls', this.menuTarget.id)
      this.syncAriaExpanded()
    }

    if (this.hasMenuTarget) {
      this.popper = createPopper(this.element, this.menuTarget, {
        placement:
          this.alignmentValue === 'right' ? 'bottom-end' : 'bottom-start',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, this.offsetValue],
            },
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements:
                this.alignmentValue === 'right' ? ['top-end'] : ['top-start'],
              padding: 8,
            },
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: 8,
              altAxis: true,
            },
          },
          {
            name: 'computeStyles',
            options: {
              adaptive: true,
              gpuAcceleration: false,
            },
          },
        ],
      })
    }
  }

  showImpersonating() {
    const alertDiv = document.getElementById('impersonating-user-message')
    if (alertDiv) {
      alertDiv.classList.remove('tw-hidden')
    }
  }

  hideImpersonating() {
    const alertDiv = document.getElementById('impersonating-user-message')
    if (alertDiv) {
      alertDiv.classList.add('tw-hidden')
    }
  }

  disconnect() {
    document.removeEventListener('click', this.boundHandleClickOutside)
    document.removeEventListener('keydown', this.boundHandleKeydown)
    if (this.popper) {
      this.popper.destroy()
    }
  }

  isOpen() {
    return (
      this.hasMenuTarget && !this.menuTarget.classList.contains('tw-hidden')
    )
  }

  getMenuItems() {
    if (!this.hasMenuTarget) return []
    return Array.from(this.menuTarget.querySelectorAll('[role="menuitem"]'))
  }

  syncAriaExpanded() {
    if (!this.triggerEl) return
    const open = this.isOpen()
    this.triggerEl.setAttribute('aria-expanded', open ? 'true' : 'false')
    if (this.hasMenuTarget) {
      this.menuTarget.setAttribute('aria-hidden', open ? 'false' : 'true')
    }
  }

  resetMenuItemTabindex() {
    this.getMenuItems().forEach((item) => item.setAttribute('tabindex', '-1'))
  }

  setActiveMenuItem(item) {
    this.getMenuItems().forEach((el) => el.setAttribute('tabindex', '-1'))
    if (item) {
      item.setAttribute('tabindex', '0')
      item.focus()
    }
  }

  open(focusTarget = 'first') {
    if (!this.hasMenuTarget) return
    this.menuTarget.classList.remove('tw-hidden')
    if (this.popper) {
      this.popper.update()
    }
    this.syncAriaExpanded()
    const items = this.getMenuItems()
    if (items.length === 0) return
    const item = focusTarget === 'last' ? items[items.length - 1] : items[0]
    this.setActiveMenuItem(item)
  }

  close(returnFocus = true) {
    if (!this.hasMenuTarget) return
    this.menuTarget.classList.add('tw-hidden')
    this.resetMenuItemTabindex()
    this.syncAriaExpanded()
    if (returnFocus && this.triggerEl) {
      this.triggerEl.focus()
    }
  }

  toggle() {
    if (this.isOpen()) {
      this.close(true)
    } else {
      this.open('first')
    }
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close(false)
    }
  }

  handleKeydown(event) {
    if (!this.hasMenuTarget) return

    const items = this.getMenuItems()
    const isTrigger = this.triggerEl && event.target === this.triggerEl
    const isMenuItem = items.includes(event.target)
    const focusInMenu = this.element.contains(document.activeElement)

    if (
      event.key === 'Escape' &&
      this.isOpen() &&
      (isTrigger || isMenuItem || focusInMenu)
    ) {
      event.preventDefault()
      this.close(true)
      return
    }

    if (!isTrigger && !isMenuItem) return

    if (isTrigger) {
      this.handleTriggerKeydown(event)
      return
    }

    if (isMenuItem && this.isOpen()) {
      this.handleMenuItemKeydown(event, items)
    }
  }

  handleTriggerKeydown(event) {
    if (this.isOpen()) return

    const { key } = event

    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault()
      this.open(key === 'ArrowUp' ? 'last' : 'first')
      return
    }

    if (key === 'Enter' || key === ' ') {
      event.preventDefault()
      this.open('first')
    }
  }

  handleMenuItemKeydown(event, items) {
    const { key } = event
    const currentIndex = items.indexOf(event.target)

    if (key === 'ArrowDown') {
      event.preventDefault()
      const next = items[currentIndex + 1]
      if (next) {
        this.setActiveMenuItem(next)
      }
      return
    }

    if (key === 'ArrowUp') {
      event.preventDefault()
      const prev = items[currentIndex - 1]
      if (prev) {
        this.setActiveMenuItem(prev)
      } else if (this.triggerEl) {
        this.resetMenuItemTabindex()
        this.triggerEl.focus()
      }
      return
    }

    if (key === 'Home') {
      event.preventDefault()
      this.setActiveMenuItem(items[0])
      return
    }

    if (key === 'End') {
      event.preventDefault()
      this.setActiveMenuItem(items[items.length - 1])
      return
    }

    if (key === 'Tab') {
      const isLast = currentIndex === items.length - 1
      const isFirst = currentIndex === 0
      if ((!event.shiftKey && isLast) || (event.shiftKey && isFirst)) {
        this.close(false)
      }
    }
  }
}
